import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Plus, Trash2, Edit2, Save, Package, TrendingUp, TrendingDown, Minus, DollarSign, Clock, BarChart3, Settings, LineChart as LineChartIcon, Gem, Award, Users, Timer, RefreshCw } from "lucide-react";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, Legend, Area, AreaChart, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis } from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtCur = (v) => v == null ? '-' : new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 2 }).format(v);
const fmtPct = (v) => v == null ? '-' : `${v.toFixed(1)}%`;
const MONTHS = ['Gen','Feb','Mar','Apr','Mag','Giu','Lug','Ago','Set','Ott','Nov','Dic'];

// ======================== PRODUCT FORM DIALOG ========================
const ProductFormDialog = ({ open, onClose, onSave, product, hourlyRate }) => {
  const [form, setForm] = useState({ name: '', cost_mode: 'standard', selling_price: '', cost_type: 'fixed', cost_value: '', hours_per_unit: '', notes: '' });
  useEffect(() => {
    if (product) setForm({ name: product.name, cost_mode: product.cost_mode || 'standard', selling_price: product.selling_price || '', cost_type: product.cost_type || 'fixed', cost_value: product.cost_value || '', hours_per_unit: product.hours_per_unit || '', notes: product.notes || '' });
    else setForm({ name: '', cost_mode: 'standard', selling_price: '', cost_type: 'fixed', cost_value: '', hours_per_unit: '', notes: '' });
  }, [product, open]);

  const isStandard = form.cost_mode === 'standard';
  const sp = parseFloat(form.selling_price) || 0;
  const cv = parseFloat(form.cost_value) || 0;
  const hrs = parseFloat(form.hours_per_unit) || 0;
  const productCost = form.cost_type === 'percentage' ? sp * (cv / 100) : cv;
  const laborCost = hrs * hourlyRate;
  const margin = sp - productCost - laborCost;
  const marginPct = sp > 0 ? (margin / sp * 100) : 0;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{product ? 'Modifica' : 'Nuovo'} Prodotto/Servizio</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label>Nome del prodotto/servizio</Label><Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="es. Consulenza strategica, Taglio capelli..." data-testid="product-name-input" /></div>
          {/* Cost mode selector */}
          <div className="grid grid-cols-2 gap-2">
            <button type="button" className={`p-3 rounded-lg border-2 text-left text-xs transition-all ${isStandard ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`} onClick={() => setForm(f => ({ ...f, cost_mode: 'standard' }))} data-testid="cost-mode-standard">
              <div className="font-bold mb-0.5">Costi Standard</div>
              <div className="text-muted-foreground">Prezzo, costo e ore fissi per ogni unita. Ogni mese inserisci solo quantita e fatturato.</div>
            </button>
            <button type="button" className={`p-3 rounded-lg border-2 text-left text-xs transition-all ${!isStandard ? 'border-orange-500 bg-orange-50' : 'border-gray-200 hover:border-gray-300'}`} onClick={() => setForm(f => ({ ...f, cost_mode: 'variable' }))} data-testid="cost-mode-variable">
              <div className="font-bold mb-0.5">Costi Variabili</div>
              <div className="text-muted-foreground">Costi e ore cambiano ogni mese. Li inserisci direttamente nei dati mensili.</div>
            </button>
          </div>
          {isStandard && (<>
            <div><Label>Prezzo di vendita per 1 unita (EUR)</Label><Input type="number" value={form.selling_price} onChange={e => setForm(f => ({ ...f, selling_price: e.target.value }))} placeholder="es. 200" data-testid="product-price-input" /><p className="text-xs text-muted-foreground mt-0.5">Quanto incassi vendendo 1 unita</p></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Tipo costo diretto</Label>
                <Select value={form.cost_type} onValueChange={v => setForm(f => ({ ...f, cost_type: v }))}>
                  <SelectTrigger data-testid="product-cost-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fixed">Valore fisso (EUR)</SelectItem>
                    <SelectItem value="percentage">% sul prezzo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Costo diretto x1</Label><Input type="number" value={form.cost_value} onChange={e => setForm(f => ({ ...f, cost_value: e.target.value }))} placeholder="es. 30" data-testid="product-cost-input" /><p className="text-xs text-muted-foreground mt-0.5">Materiali, fornitori per 1 unita</p></div>
            </div>
            <div><Label>Ore di lavoro per 1 unita</Label><Input type="number" step="0.5" value={form.hours_per_unit} onChange={e => setForm(f => ({ ...f, hours_per_unit: e.target.value }))} placeholder="es. 2" data-testid="product-hours-input" /><p className="text-xs text-muted-foreground mt-0.5">Quante ore servono per erogare 1 unita (costo orario: {fmtCur(hourlyRate)}/h)</p></div>
            {/* Live margin preview */}
            <div className={`p-3 rounded-lg border-2 ${margin >= 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
              <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1">Margine teorico per 1 unita</div>
              <div className="grid grid-cols-4 gap-2 text-center text-xs">
                <div><div className="text-muted-foreground">Prezzo</div><div className="font-bold">{fmtCur(sp)}</div></div>
                <div><div className="text-muted-foreground">Costo diretto</div><div className="font-bold">{fmtCur(productCost)}</div></div>
                <div><div className="text-muted-foreground">Costo lavoro</div><div className="font-bold">{fmtCur(laborCost)}</div></div>
                <div><div className="text-muted-foreground">Margine</div><div className={`font-black text-base ${margin >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>{fmtCur(margin)} <span className="text-xs">({fmtPct(marginPct)})</span></div></div>
              </div>
            </div>
          </>)}
          {!isStandard && (
            <div className="p-3 rounded-lg bg-orange-50 border border-orange-200 text-xs text-orange-800">
              Il margine verra calcolato dai dati mensili che inserirai: fatturato, costi diretti, ore lavorate e quantita.
            </div>
          )}
          <div><Label>Note</Label><Input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Opzionale" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={() => onSave(form)} disabled={!form.name || (isStandard && !form.selling_price)} data-testid="save-product-btn"><Save className="h-4 w-4 mr-1" />Salva</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ======================== MONTHLY DATA DIALOG ========================
const MonthlyDataDialog = ({ open, onClose, product, clientId, year }) => {
  const [data, setData] = useState({});
  const [prices, setPrices] = useState({});
  const [saving, setSaving] = useState(false);
  const [savedMonths, setSavedMonths] = useState(new Set());
  const isVariable = product?.cost_mode === 'variable';
  const defaultPrice = product?.selling_price || 0;

  useEffect(() => {
    if (open && product) {
      setSavedMonths(new Set());
      setPrices({});
      axios.get(`${API}/clients/${clientId}/products/${product.id}/monthly`, { params: { year } })
        .then(res => {
          const map = {};
          const priceMap = {};
          res.data.data.forEach(d => {
            map[d.month] = d;
            if (!isVariable && d.quantity > 0 && d.revenue > 0) {
              priceMap[d.month] = (d.revenue / d.quantity).toFixed(2);
            }
          });
          setData(map);
          setPrices(priceMap);
        }).catch(() => {});
    }
  }, [open, product, clientId, year, isVariable]);

  const saveMonth = async (month) => {
    const d = data[month];
    if (!d) return;
    const qty = parseInt(d.quantity) || 0;
    const price = parseFloat(prices[month]) || defaultPrice;
    const computedRevenue = isVariable ? (parseFloat(d.revenue) || 0) : qty * price;
    const hoursPerUnit = product?.hours_per_unit || 0;
    const computedHours = isVariable ? (parseFloat(d.hours_worked) || 0) : qty * hoursPerUnit;
    await axios.post(`${API}/clients/${clientId}/products/${product.id}/monthly`, {
      year, month,
      revenue: computedRevenue,
      quantity: qty,
      hours_worked: computedHours,
      direct_costs: parseFloat(d.direct_costs) || 0,
      cost_override: d.cost_override ? parseFloat(d.cost_override) : null,
    });
    setSavedMonths(prev => new Set([...prev, month]));
  };

  const saveAll = async () => {
    setSaving(true);
    try {
      const monthsToSave = Object.entries(data).filter(([_, d]) => d && (d.revenue || d.quantity || d.hours_worked || d.direct_costs));
      for (const [month] of monthsToSave) {
        await saveMonth(parseInt(month));
      }
      toast.success(`${monthsToSave.length} mesi salvati`);
    } catch { toast.error("Errore nel salvataggio"); }
    finally { setSaving(false); }
  };

  const updateField = (month, field, value) => {
    setSavedMonths(prev => { const n = new Set(prev); n.delete(month); return n; });
    setData(prev => ({ ...prev, [month]: { ...(prev[month] || { revenue: 0, quantity: 0, hours_worked: 0, direct_costs: 0 }), month, [field]: value } }));
  };

  const updatePrice = (month, value) => {
    setSavedMonths(prev => { const n = new Set(prev); n.delete(month); return n; });
    setPrices(prev => ({ ...prev, [month]: value }));
  };

  if (!product) return null;
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-auto">
        <DialogHeader>
          <DialogTitle>Dati Mensili — {product.name} ({year})</DialogTitle>
          <p className="text-sm text-muted-foreground">
            {isVariable
              ? 'Per ogni mese inserisci fatturato, costi diretti, ore lavorate e quantita.'
              : `Inserisci la quantita venduta per ogni mese. Il prezzo unitario e' pre-compilato (${fmtCur(defaultPrice)}), puoi modificarlo se necessario.`}
          </p>
        </DialogHeader>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead className="w-16">Mese</TableHead>
              {isVariable ? (
                <>
                  <TableHead className="text-right">Fatturato (EUR)</TableHead>
                  <TableHead className="text-right">Qta vendute</TableHead>
                  <TableHead className="text-right">Costi diretti (EUR)</TableHead>
                  <TableHead className="text-right">Ore lavorate</TableHead>
                </>
              ) : (
                <>
                  <TableHead className="text-right">Qta vendute</TableHead>
                  <TableHead className="text-right">Prezzo unit.</TableHead>
                  <TableHead className="text-right">= Fatturato</TableHead>
                </>
              )}
            </TableRow></TableHeader>
            <TableBody>
              {MONTHS.map((m, i) => {
                const month = i + 1;
                const d = data[month] || {};
                const qty = parseInt(d.quantity) || 0;
                const price = parseFloat(prices[month]) || defaultPrice;
                const revenue = qty * price;
                const hasData = d.revenue || d.quantity || d.hours_worked || d.direct_costs;
                return (
                  <TableRow key={i} className={hasData ? 'bg-slate-50' : ''}>
                    <TableCell className="font-medium">{m}</TableCell>
                    {isVariable ? (
                      <>
                        <TableCell><Input type="number" className="h-7 text-xs text-right w-24" value={d.revenue ?? ''} onChange={e => updateField(month, 'revenue', e.target.value)} placeholder="0" data-testid={`monthly-revenue-${month}`} /></TableCell>
                        <TableCell><Input type="number" className="h-7 text-xs text-right w-20" value={d.quantity ?? ''} onChange={e => updateField(month, 'quantity', e.target.value)} placeholder="0" data-testid={`monthly-qty-${month}`} /></TableCell>
                        <TableCell><Input type="number" className="h-7 text-xs text-right w-24" value={d.direct_costs ?? ''} onChange={e => updateField(month, 'direct_costs', e.target.value)} placeholder="0" data-testid={`monthly-costs-${month}`} /></TableCell>
                        <TableCell><Input type="number" className="h-7 text-xs text-right w-20" value={d.hours_worked ?? ''} onChange={e => updateField(month, 'hours_worked', e.target.value)} placeholder="0" data-testid={`monthly-hours-${month}`} /></TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell><Input type="number" className="h-7 text-xs text-right w-20" value={d.quantity ?? ''} onChange={e => updateField(month, 'quantity', e.target.value)} placeholder="0" data-testid={`monthly-qty-${month}`} /></TableCell>
                        <TableCell><Input type="number" step="0.01" className="h-7 text-xs text-right w-24" value={prices[month] ?? ''} onChange={e => updatePrice(month, e.target.value)} placeholder={defaultPrice.toFixed(2)} data-testid={`monthly-price-${month}`} /></TableCell>
                        <TableCell className="text-right text-xs font-semibold tabular-nums text-muted-foreground" data-testid={`monthly-revenue-${month}`}>{qty > 0 ? fmtCur(revenue) : '-'}</TableCell>
                      </>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>Chiudi</Button>
          <Button onClick={saveAll} disabled={saving} data-testid="save-all-months-btn"><Save className="h-4 w-4 mr-1" />{saving ? 'Salvataggio...' : 'Salva tutto'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ======================== BUNDLE DIALOG (with catalog drill-down) ========================
const BundleDialog = ({ open, onClose, onSave, products, catalog, hourlyRate }) => {
  const [name, setName] = useState('');
  const [selectedCategories, setSelectedCategories] = useState({});
  const [specificServices, setSpecificServices] = useState({});
  const [bundlePrice, setBundlePrice] = useState('');
  const [expandedCat, setExpandedCat] = useState(null);

  const toggleCategory = (catProduct) => {
    setSelectedCategories(prev => {
      const next = { ...prev };
      if (next[catProduct.id]) { delete next[catProduct.id]; }
      else { next[catProduct.id] = catProduct; }
      return next;
    });
  };

  const selectService = (productId, service) => {
    setSpecificServices(prev => ({ ...prev, [productId]: service }));
    setExpandedCat(null);
  };

  const clearService = (productId) => {
    setSpecificServices(prev => { const n = { ...prev }; delete n[productId]; return n; });
  };

  // Compute bundle totals
  const selectedList = Object.values(selectedCategories);
  const computeServiceCost = (svc) => (svc.duration_minutes || 0) * (hourlyRate / 60);

  let totalPrice = 0, totalCost = 0, totalDuration = 0;
  selectedList.forEach(cat => {
    const svc = specificServices[cat.id];
    if (svc) {
      totalPrice += svc.selling_price || 0;
      totalCost += computeServiceCost(svc);
      totalDuration += svc.duration_minutes || 0;
    } else {
      totalPrice += cat.selling_price || 0;
      totalCost += cat.total_cost || 0;
      totalDuration += (cat.hours_per_unit || 0) * 60;
    }
  });
  const bp = parseFloat(bundlePrice) || totalPrice;
  const bundleMargin = bp - totalCost;
  const bundleMarginPct = bp > 0 ? (bundleMargin / bp * 100) : 0;

  const getCatalogForCategory = (catName) => {
    if (!catalog?.grouped) return [];
    return catalog.grouped[catName] || [];
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-auto">
        <DialogHeader>
          <DialogTitle>Crea Bundle Servizi</DialogTitle>
          <p className="text-sm text-muted-foreground">Seleziona le categorie e scegli il servizio specifico per ogni categoria per vedere il margine esatto.</p>
        </DialogHeader>
        <div className="space-y-4">
          <div><Label>Nome bundle</Label><Input value={name} onChange={e => setName(e.target.value)} placeholder="es. Pacchetto Relax" data-testid="bundle-name-input" /></div>

          {/* Category selection */}
          <div className="space-y-2">
            <Label>Seleziona categorie</Label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {products.map(cat => {
                const isSelected = !!selectedCategories[cat.id];
                const svc = specificServices[cat.id];
                return (
                  <div key={cat.id} className={`border-2 rounded-lg p-2 cursor-pointer transition-all ${isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`} onClick={() => toggleCategory(cat)} data-testid={`cat-toggle-${cat.name}`}>
                    <div className="font-bold text-sm">{cat.name}</div>
                    <div className="text-[10px] text-muted-foreground">Media: {fmtCur(cat.selling_price)} | {Math.round((cat.hours_per_unit || 0) * 60)} min</div>
                    <div className={`text-xs font-bold ${cat.margin >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>Margine: {fmtCur(cat.margin)} ({fmtPct(cat.margin_pct)})</div>
                    {isSelected && svc && <div className="mt-1 text-[10px] text-blue-700 font-semibold truncate">{svc.name}</div>}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Specific service selection for each selected category */}
          {selectedList.length > 0 && (
            <div className="space-y-2">
              <Label>Dettaglio servizi nel bundle</Label>
              {selectedList.map(cat => {
                const svc = specificServices[cat.id];
                const isExpanded = expandedCat === cat.id;
                const catalogItems = getCatalogForCategory(cat.name);
                return (
                  <div key={cat.id} className="border rounded-lg overflow-hidden">
                    <div className="flex items-center justify-between p-2 bg-slate-50 cursor-pointer" onClick={() => setExpandedCat(isExpanded ? null : cat.id)} data-testid={`expand-cat-${cat.name}`}>
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-sm">{cat.name}</span>
                        {svc ? (
                          <Badge variant="secondary" className="text-[10px]">{svc.name} ({svc.gender})</Badge>
                        ) : (
                          <Badge variant="outline" className="text-[10px] text-amber-600 border-amber-300">Usa media — clicca per scegliere</Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {svc && <Button size="sm" variant="ghost" className="h-6 px-1" onClick={e => { e.stopPropagation(); clearService(cat.id); }}><Trash2 className="h-3 w-3 text-red-400" /></Button>}
                        <span className="text-xs text-muted-foreground">{isExpanded ? '▲' : '▼'}</span>
                      </div>
                    </div>
                    {isExpanded && (
                      <div className="max-h-48 overflow-auto">
                        <Table>
                          <TableHeader><TableRow className="text-[10px]">
                            <TableHead className="py-1">Servizio</TableHead>
                            <TableHead className="py-1 text-right">Prezzo</TableHead>
                            <TableHead className="py-1 text-right">Durata</TableHead>
                            <TableHead className="py-1 text-right">Costo</TableHead>
                            <TableHead className="py-1 text-right">Margine</TableHead>
                          </TableRow></TableHeader>
                          <TableBody>
                            {catalogItems.map(item => {
                              const cost = computeServiceCost(item);
                              const margin = (item.selling_price || 0) - cost;
                              const isActive = svc?.id === item.id;
                              return (
                                <TableRow key={item.id} className={`cursor-pointer text-xs ${isActive ? 'bg-blue-100' : 'hover:bg-slate-50'}`} onClick={() => selectService(cat.id, item)} data-testid={`select-svc-${item.id}`}>
                                  <TableCell className="py-1 font-medium">{item.name} <span className="text-muted-foreground">({item.gender})</span></TableCell>
                                  <TableCell className="py-1 text-right tabular-nums">{fmtCur(item.selling_price)}</TableCell>
                                  <TableCell className="py-1 text-right tabular-nums">{item.duration_minutes} min</TableCell>
                                  <TableCell className="py-1 text-right tabular-nums">{fmtCur(cost)}</TableCell>
                                  <TableCell className={`py-1 text-right tabular-nums font-bold ${margin >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>{fmtCur(margin)} ({item.selling_price > 0 ? fmtPct(margin / item.selling_price * 100) : '-'})</TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Bundle price override */}
          {selectedList.length > 0 && (
            <div><Label>Prezzo bundle (lascia vuoto = somma prezzi)</Label><Input type="number" value={bundlePrice} onChange={e => setBundlePrice(e.target.value)} placeholder={totalPrice.toFixed(2)} /></div>
          )}

          {/* Totals */}
          {selectedList.length > 0 && (
            <div className={`p-3 rounded-lg border-2 ${bundleMargin >= 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
              <div className="grid grid-cols-5 gap-2 text-center text-xs">
                <div><div className="text-muted-foreground">Prezzo totale</div><div className="font-bold tabular-nums">{fmtCur(totalPrice)}</div></div>
                <div><div className="text-muted-foreground">Prezzo bundle</div><div className="font-bold tabular-nums">{fmtCur(bp)}</div></div>
                <div><div className="text-muted-foreground">Durata</div><div className="font-bold tabular-nums">{Math.round(totalDuration)} min</div></div>
                <div><div className="text-muted-foreground">Costo</div><div className="font-bold tabular-nums">{fmtCur(totalCost)}</div></div>
                <div><div className="text-muted-foreground">Margine</div><div className={`font-black text-base ${bundleMargin >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>{fmtCur(bundleMargin)}<div className="text-[10px] font-normal">{fmtPct(bundleMarginPct)}</div></div></div>
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={() => {
            const items_data = selectedList.map(cat => {
              const svc = specificServices[cat.id];
              return { product_id: cat.id, quantity: 1, specific_service: svc ? { id: svc.id, name: svc.name, price: svc.selling_price, duration: svc.duration_minutes } : null };
            });
            onSave({
              name,
              product_ids: items_data.map(i => i.product_id),
              quantities: items_data.map(() => 1),
              bundle_price: bundlePrice ? parseFloat(bundlePrice) : null,
              specific_services: items_data.reduce((acc, it) => { if (it.specific_service) acc[it.product_id] = it.specific_service; return acc; }, {}),
            });
          }} disabled={!name || selectedList.length < 2} data-testid="save-bundle-btn"><Package className="h-4 w-4 mr-1" />Crea Bundle</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ======================== MIX OPTIMIZER ========================
const MixOptimizer = ({ metrics, calcMix, currentAlloc, optimalAlloc, balancedAlloc, totalHours, periodStructure, msc, numMonths, colors }) => {
  const [alloc, setAlloc] = useState(currentAlloc);
  const [activeStrategy, setActiveStrategy] = useState('current');

  const applyStrategy = (strategy, allocData) => {
    setActiveStrategy(strategy);
    setAlloc({ ...allocData });
  };

  const updateAlloc = (id, value) => {
    setActiveStrategy('custom');
    const val = Math.max(0, Math.min(100, parseInt(value) || 0));
    setAlloc(prev => ({ ...prev, [id]: val }));
  };

  const result = calcMix(alloc);
  const totalPct = Object.values(alloc).reduce((s, v) => s + v, 0);
  const currentResult = calcMix(currentAlloc);
  const diffMargin = result.totMargin - currentResult.totMargin;
  const diffProfit = result.profit - currentResult.profit;

  return (
    <div className="space-y-4">
      {/* Strategy buttons */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm font-bold">Strategia:</span>
        <Button size="sm" variant={activeStrategy === 'current' ? 'default' : 'outline'} onClick={() => applyStrategy('current', currentAlloc)}>Mix Attuale</Button>
        <Button size="sm" variant={activeStrategy === 'optimal' ? 'default' : 'outline'} onClick={() => applyStrategy('optimal', optimalAlloc)} className={activeStrategy === 'optimal' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}>Massimo Margine</Button>
        <Button size="sm" variant={activeStrategy === 'balanced' ? 'default' : 'outline'} onClick={() => applyStrategy('balanced', balancedAlloc)}>Bilanciato</Button>
        {activeStrategy === 'custom' && <Badge variant="outline">Personalizzato</Badge>}
      </div>

      {/* Sliders + Results side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Allocation sliders */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-2"><CardTitle className="text-base">Allocazione ore ({totalHours}h disponibili)</CardTitle>
            {totalPct !== 100 && <p className="text-xs text-amber-600 font-bold">Totale: {totalPct}% (deve essere 100%)</p>}
          </CardHeader>
          <CardContent className="space-y-3">
            {result.details.map((p, i) => (
              <div key={p.id} className="space-y-1">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: p.color }} />
                    <span className="text-sm font-semibold">{p.name}</span>
                    <span className="text-[10px] text-muted-foreground">({fmtCur(p.margin_per_hour)}/h)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Input type="number" className="w-16 h-6 text-xs text-right" value={alloc[p.id] || 0} onChange={e => updateAlloc(p.id, e.target.value)} min={0} max={100} />
                    <span className="text-xs text-muted-foreground w-4">%</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <input type="range" min="0" max="100" value={alloc[p.id] || 0} onChange={e => updateAlloc(p.id, e.target.value)} className="flex-1 h-2 accent-current" style={{ color: p.color }} />
                </div>
                <div className="flex justify-between text-[10px] text-muted-foreground">
                  <span>{Math.round(p.hours)}h → {p.units} unita</span>
                  <span>Fatt. {fmtCur(p.revenue)} → Marg. <span className={p.margin >= 0 ? 'text-emerald-700 font-bold' : 'text-red-700 font-bold'}>{fmtCur(p.margin)}</span></span>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Results panel */}
        <Card className={`border-2 ${result.sustainable ? 'border-emerald-300 bg-emerald-50/50' : 'border-red-300 bg-red-50/50'}`}>
          <CardHeader className="pb-2"><CardTitle className="text-base">Risultato Mix</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-2">
              <div className="flex justify-between text-sm"><span className="text-muted-foreground">Fatturato</span><span className="font-bold tabular-nums">{fmtCur(result.totRev)}</span></div>
              <div className="flex justify-between text-sm"><span className="text-muted-foreground">Margine lordo</span><span className="font-bold tabular-nums text-emerald-700">{fmtCur(result.totMargin)}</span></div>
              <div className="flex justify-between text-sm"><span className="text-muted-foreground">Struttura ({numMonths}m)</span><span className="font-bold tabular-nums text-orange-700">-{fmtCur(periodStructure)}</span></div>
              <hr />
              <div className="flex justify-between text-base"><span className="font-bold">Utile finale</span><span className={`font-black text-lg ${result.sustainable ? 'text-emerald-800' : 'text-red-800'}`}>{fmtCur(result.profit)}</span></div>
            </div>
            {/* Comparison vs current */}
            <div className={`p-2 rounded text-center text-xs ${diffProfit >= 0 ? 'bg-emerald-100' : 'bg-red-100'}`}>
              <div className="text-muted-foreground">vs Mix Attuale</div>
              <div className={`font-black text-sm ${diffProfit >= 0 ? 'text-emerald-800' : 'text-red-800'}`}>{diffProfit >= 0 ? '+' : ''}{fmtCur(diffProfit)}</div>
            </div>
            {/* Breakdown bar */}
            <div>
              <div className="text-[10px] text-muted-foreground mb-1">Composizione margine</div>
              <div className="flex h-6 rounded-lg overflow-hidden">
                {result.details.filter(p => p.margin > 0).map(p => (
                  <div key={p.id} style={{ width: `${(p.margin / result.totMargin * 100)}%`, backgroundColor: p.color }} className="flex items-center justify-center text-[9px] text-white font-bold truncate px-1">{Math.round(p.margin / result.totMargin * 100)}%</div>
                ))}
              </div>
              <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1">
                {result.details.map(p => (
                  <div key={p.id} className="flex items-center gap-1 text-[10px]"><span className="w-2 h-2 rounded-full" style={{ backgroundColor: p.color }} />{p.name}</div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Detail table */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Dettaglio per servizio</CardTitle></CardHeader>
        <CardContent>
          <Table>
            <TableHeader><TableRow>
              <TableHead>Servizio</TableHead><TableHead className="text-right">% Ore</TableHead><TableHead className="text-right">Ore</TableHead><TableHead className="text-right">Unita</TableHead><TableHead className="text-right">Fatturato</TableHead><TableHead className="text-right">Margine</TableHead><TableHead className="text-right">Margine %</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {result.details.map(p => (
                <TableRow key={p.id}>
                  <TableCell className="font-semibold"><div className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: p.color }} />{p.name}</div></TableCell>
                  <TableCell className="text-right tabular-nums">{p.alloc_pct}%</TableCell>
                  <TableCell className="text-right tabular-nums">{Math.round(p.hours)}h</TableCell>
                  <TableCell className="text-right tabular-nums">{p.units}</TableCell>
                  <TableCell className="text-right tabular-nums">{fmtCur(p.revenue)}</TableCell>
                  <TableCell className={`text-right tabular-nums font-bold ${p.margin >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>{fmtCur(p.margin)}</TableCell>
                  <TableCell className="text-right tabular-nums">{p.revenue > 0 ? fmtPct(p.margin / p.revenue * 100) : '-'}</TableCell>
                </TableRow>
              ))}
              <TableRow className="border-t-2 font-bold">
                <TableCell>Totale</TableCell>
                <TableCell className="text-right tabular-nums">{totalPct}%</TableCell>
                <TableCell className="text-right tabular-nums">{Math.round(result.totHrs)}h</TableCell>
                <TableCell className="text-right tabular-nums">{result.details.reduce((s, p) => s + p.units, 0)}</TableCell>
                <TableCell className="text-right tabular-nums">{fmtCur(result.totRev)}</TableCell>
                <TableCell className={`text-right tabular-nums ${result.totMargin >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>{fmtCur(result.totMargin)}</TableCell>
                <TableCell className="text-right tabular-nums">{result.totRev > 0 ? fmtPct(result.totMargin / result.totRev * 100) : '-'}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

// ======================== MAIN COMPONENT ========================
export const ProductMarginPage = ({ clientId, year, clientName }) => {
  const [products, setProducts] = useState([]);
  const [rankings, setRankings] = useState(null);
  const [bundles, setBundles] = useState([]);
  const [hourlyRate, setHourlyRate] = useState(25);
  const [rateInput, setRateInput] = useState('25');
  const [structureCost, setStructureCost] = useState(0);
  const [structureCostInput, setStructureCostInput] = useState('0');
  const [loading, setLoading] = useState(true);
  const [showProductForm, setShowProductForm] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [showMonthly, setShowMonthly] = useState(false);
  const [monthlyProduct, setMonthlyProduct] = useState(null);
  const [showBundle, setShowBundle] = useState(false);
  const [activeTab, setActiveTab] = useState('products');
  const [catalog, setCatalog] = useState(null);
  // Whale curve data per calcolare il costo-to-serve medio (per test break-even Diamante)
  const [wcCostToServe, setWcCostToServe] = useState(0);
  useEffect(() => {
    axios.get(`${API}/clients/${clientId}/whale-curve`, { params: { year } })
      .then(r => {
        const d = r.data || {};
        const customers = d.customers || [];
        // Somma allocated_costs dai clienti (i campi top-level total_fixed_costs_* sono null)
        const totalFC = customers.reduce((acc, c) => acc + (c.allocated_costs || 0), 0);
        const nCust = customers.length;
        setWcCostToServe(nCust > 0 ? totalFC / nCust : 0);
      })
      .catch(() => setWcCostToServe(0));
  }, [clientId, year]);

  // Spiragli Excel import
  const [showImportExcel, setShowImportExcel] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [importBusy, setImportBusy] = useState(false);
  const [importSelectedMonths, setImportSelectedMonths] = useState({});
  // Sync da Fatture & Servizi (per clienti che usano il flusso XML fatture)
  const [hasServiceInvoices, setHasServiceInvoices] = useState(false);
  const [syncingServices, setSyncingServices] = useState(false);
  useEffect(() => {
    axios.get(`${API}/clients/${clientId}/service-invoices/summary`, { params: { year } })
      .then(r => setHasServiceInvoices((r.data?.summary?.n_invoices || 0) > 0))
      .catch(() => setHasServiceInvoices(false));
  }, [clientId, year]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [prodRes, rankRes, bundleRes, rateRes, catRes] = await Promise.all([
        axios.get(`${API}/clients/${clientId}/products`),
        axios.get(`${API}/clients/${clientId}/products/rankings`, { params: { year } }),
        axios.get(`${API}/clients/${clientId}/product-bundles`),
        axios.get(`${API}/clients/${clientId}/labor-rate`),
        axios.get(`${API}/clients/${clientId}/service-catalog`).catch(() => ({ data: { services: [], grouped: {} } })),
      ]);
      setProducts(prodRes.data.products);
      setRankings(rankRes.data);
      setBundles(bundleRes.data.bundles);
      setHourlyRate(rateRes.data.hourly_rate);
      setRateInput(rateRes.data.hourly_rate.toString());
      setStructureCost(rateRes.data.monthly_structure_cost || 0);
      setStructureCostInput((rateRes.data.monthly_structure_cost || 0).toString());
      setCatalog(catRes.data);
    } catch { toast.error("Errore caricamento"); }
    finally { setLoading(false); }
  }, [clientId, year]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const saveProduct = async (form) => {
    try {
      const payload = { ...form, selling_price: parseFloat(form.selling_price) || 0, cost_value: parseFloat(form.cost_value) || 0, hours_per_unit: parseFloat(form.hours_per_unit) || 0 };
      if (editProduct) await axios.put(`${API}/clients/${clientId}/products/${editProduct.id}`, payload);
      else await axios.post(`${API}/clients/${clientId}/products`, payload);
      toast.success(editProduct ? 'Aggiornato' : 'Creato');
      setShowProductForm(false); setEditProduct(null); fetchAll();
    } catch { toast.error("Errore"); }
  };

  const deleteProduct = async (id) => {
    if (!window.confirm("Eliminare questo prodotto/servizio?")) return;
    try { await axios.delete(`${API}/clients/${clientId}/products/${id}`); toast.success("Eliminato"); fetchAll(); } catch { toast.error("Errore"); }
  };

  const saveRate = async () => {
    try { await axios.put(`${API}/clients/${clientId}/labor-rate`, { hourly_rate: parseFloat(rateInput), monthly_structure_cost: parseFloat(structureCostInput) || 0 }); toast.success("Parametri aggiornati"); setHourlyRate(parseFloat(rateInput)); setStructureCost(parseFloat(structureCostInput) || 0); fetchAll(); } catch { toast.error("Errore"); }
  };

  const saveBundle = async (form) => {
    try { await axios.post(`${API}/clients/${clientId}/product-bundles`, form); toast.success("Bundle creato"); setShowBundle(false); fetchAll(); } catch { toast.error("Errore"); }
  };

  const deleteBundle = async (id) => {
    try { await axios.delete(`${API}/clients/${clientId}/product-bundles/${id}`); toast.success("Eliminato"); fetchAll(); } catch { toast.error("Errore"); }
  };

  const syncFromServiceInvoices = async () => {
    if (!window.confirm(`Vuoi sincronizzare i dati di Margine Prodotti dai dati in Fatture & Servizi per l'anno ${year}?\n\nVerranno:\n• Creati/aggiornati i prodotti (uno per servizio)\n• Popolati i dati mensili (fatturato, ore, materiale)\n• Aggiornato il costo orario col valore reale calcolato dal bilancio`)) return;
    setSyncingServices(true);
    try {
      const r = await axios.post(`${API}/clients/${clientId}/service-margin/sync-to-products`, null, { params: { year } });
      const d = r.data || {};
      toast.success(`Sync completato: ${d.services_synced?.length || 0} servizi, ${d.monthly_records_upserted || 0} record mensili. Costo orario: ${d.hourly_rate_calculated}€/h`);
      await fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore sync");
    } finally {
      setSyncingServices(false);
    }
  };

  if (loading) return <div className="py-12 text-center text-muted-foreground">Caricamento...</div>;

  const classColors = { profit: 'text-emerald-700 bg-emerald-50 border-emerald-200', loss: 'text-red-700 bg-red-50 border-red-200', neutral: 'text-amber-700 bg-amber-50 border-amber-200' };
  const classIcons = { profit: TrendingUp, loss: TrendingDown, neutral: Minus };

  return (
    <div className="space-y-4" data-testid="product-margin-page">
      {/* Labor rate config */}
      <div className="flex items-center justify-between bg-slate-50 rounded-lg p-3 border">
        <div className="flex items-center gap-3 flex-wrap">
          <Settings className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium">Costo orario:</span>
          <Input type="number" className="w-20 h-7 text-sm text-right" value={rateInput} onChange={e => setRateInput(e.target.value)} data-testid="hourly-rate-input" />
          <span className="text-sm text-muted-foreground">EUR/ora</span>
          <span className="text-muted-foreground">|</span>
          <span className="text-sm font-medium">Costo struttura mensile:</span>
          <Input type="number" className="w-24 h-7 text-sm text-right" value={structureCostInput} onChange={e => setStructureCostInput(e.target.value)} data-testid="structure-cost-input" />
          <span className="text-sm text-muted-foreground">EUR/mese</span>
          {(parseFloat(rateInput) !== hourlyRate || parseFloat(structureCostInput) !== structureCost) && <Button size="sm" onClick={saveRate} data-testid="save-rate-btn"><Save className="h-3.5 w-3.5 mr-1" />Salva</Button>}
        </div>
        <div className="flex gap-2">
          <Button size="sm" onClick={() => { setEditProduct(null); setShowProductForm(true); }} data-testid="add-product-btn"><Plus className="h-3.5 w-3.5 mr-1" />Prodotto</Button>
          <Button size="sm" variant="outline" onClick={() => setShowBundle(true)} disabled={products.length < 2} data-testid="add-bundle-btn"><Package className="h-3.5 w-3.5 mr-1" />Bundle</Button>
          {hasServiceInvoices && (
            <Button
              size="sm"
              variant="outline"
              className="border-sky-300 text-sky-700 hover:bg-sky-50"
              disabled={syncingServices}
              onClick={syncFromServiceInvoices}
              data-testid="sync-from-services-btn"
              title="Sincronizza automaticamente prodotti, ore, materiale e costo orario dai dati caricati in Fatture & Servizi (Excel Claudia + acquisti fornitori)"
            >
              <RefreshCw className={`h-3.5 w-3.5 mr-1 ${syncingServices ? 'animate-spin' : ''}`} />
              {syncingServices ? "Sincronizzo…" : "Sync da Fatture & Servizi"}
            </Button>
          )}
          {(clientName || '').toLowerCase().includes('spiragli') && (
            <Button size="sm" variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" onClick={() => setShowImportExcel(true)} data-testid="import-spiragli-excel-btn">
              <Package className="h-3.5 w-3.5 mr-1" />Importa Excel Spiragli
            </Button>
          )}
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="products" data-testid="tab-prod-list">Prodotti ({products.length})</TabsTrigger>
          <TabsTrigger value="trends" data-testid="tab-prod-trends">Andamento</TabsTrigger>
          <TabsTrigger value="rankings" data-testid="tab-prod-rankings">Classifiche</TabsTrigger>
          <TabsTrigger value="bundles" data-testid="tab-prod-bundles">Ottimizza Mix</TabsTrigger>
        </TabsList>

        {/* ==================== PRODUCTS LIST ==================== */}
        <TabsContent value="products" className="space-y-3">
          {products.length === 0 ? (
            <Card><CardContent className="py-12 text-center"><DollarSign className="h-12 w-12 mx-auto text-muted-foreground mb-3" /><h3 className="text-lg font-semibold">Nessun prodotto/servizio</h3><p className="text-muted-foreground">Aggiungi i tuoi prodotti per calcolare i margini</p></CardContent></Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {products.map(p => {
                const isVar = p.cost_mode === 'variable';
                const hasMargin = p.margin != null;
                const Icon = hasMargin && p.margin >= 0 ? TrendingUp : TrendingDown;
                const borderColor = isVar ? 'border-orange-200' : (hasMargin && p.margin >= 0 ? 'border-emerald-200' : 'border-red-200');
                return (
                  <Card key={p.id} className={`border-2 ${borderColor}`} data-testid={`product-card-${p.id}`}>
                    <CardContent className="pt-4 pb-3">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <div className="flex items-center gap-2">
                            <h4 className="font-bold">{p.name}</h4>
                            <Badge variant="outline" className={`text-[10px] ${isVar ? 'bg-orange-50 text-orange-700 border-orange-300' : 'bg-blue-50 text-blue-700 border-blue-300'}`}>{isVar ? 'Variabile' : 'Standard'}</Badge>
                          </div>
                          {p.notes && <p className="text-xs text-muted-foreground">{p.notes}</p>}
                        </div>
                        <div className="flex gap-1">
                          <Button size="sm" variant="ghost" onClick={() => { setEditProduct(p); setShowProductForm(true); }} data-testid={`edit-product-${p.id}`}><Edit2 className="h-3.5 w-3.5" /></Button>
                          <Button size="sm" variant="ghost" onClick={() => deleteProduct(p.id)} data-testid={`delete-product-${p.id}`}><Trash2 className="h-3.5 w-3.5 text-red-400" /></Button>
                        </div>
                      </div>
                      {isVar ? (
                        <div className="p-2 rounded bg-orange-50 text-xs text-orange-800 mb-3">Costi e ore variabili — il margine viene calcolato dai dati mensili</div>
                      ) : (
                        <div className="grid grid-cols-5 gap-2 text-center text-xs mb-3">
                          <div><div className="text-muted-foreground">Prezzo</div><div className="font-bold tabular-nums">{fmtCur(p.selling_price)}</div></div>
                          <div><div className="text-muted-foreground">Costo prod.</div><div className="font-bold tabular-nums">{fmtCur(p.product_cost)}</div></div>
                          <div><div className="text-muted-foreground">Lavoro ({p.hours_per_unit}h)</div><div className="font-bold tabular-nums">{fmtCur(p.labor_cost)}</div></div>
                          <div><div className="text-muted-foreground">Margine</div><div className={`font-black tabular-nums ${p.margin >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>{fmtCur(p.margin)}</div></div>
                          <div><div className="text-muted-foreground">Margine %</div><div className={`font-black tabular-nums flex items-center justify-center gap-0.5 ${p.margin >= 0 ? 'text-emerald-700' : 'text-red-700'}`}><Icon className="h-3.5 w-3.5" />{fmtPct(p.margin_pct)}</div></div>
                        </div>
                      )}
                      <Button size="sm" variant="outline" className="w-full" onClick={() => { setMonthlyProduct(p); setShowMonthly(true); }} data-testid={`monthly-data-${p.id}`}><BarChart3 className="h-3.5 w-3.5 mr-2" />{isVar ? 'Inserisci dati mensili (fatturato, costi, ore, quantita)' : 'Inserisci dati mensili (quantita, fatturato)'}</Button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* ==================== TREND / ANDAMENTO ==================== */}
        <TabsContent value="trends" className="space-y-4" data-testid="trends-section">
          {(() => {
            const prodsWithData = rankings?.products?.filter(p => p.has_monthly_data && p.monthly_data?.length > 0) || [];
            if (prodsWithData.length === 0) return (
              <Card><CardContent className="py-12 text-center"><LineChartIcon className="h-12 w-12 mx-auto text-muted-foreground mb-3" /><h3 className="text-lg font-semibold">Nessun dato mensile</h3><p className="text-muted-foreground">Inserisci i dati mensili dei prodotti per vedere l'andamento</p></CardContent></Card>
            );
            const COLORS = ['#10B981','#3B82F6','#F59E0B','#EF4444','#8B5CF6','#EC4899','#14B8A6','#F97316','#6366F1','#84CC16'];
            const chartData = MONTHS.map((mName, i) => {
              const row = { month: mName };
              prodsWithData.forEach(p => {
                const md = p.monthly_data.find(d => d.month === i + 1);
                row[`rev_${p.id}`] = md ? md.revenue : null;
                row[`mar_${p.id}`] = md ? md.margin : null;
                row[`mar_pct_${p.id}`] = md ? md.margin_pct : null;
                row[`qty_${p.id}`] = md ? md.quantity : null;
              });
              return row;
            });
            const hasAnyData = chartData.some(r => prodsWithData.some(p => r[`rev_${p.id}`] != null));
            if (!hasAnyData) return (
              <Card><CardContent className="py-12 text-center"><p className="text-muted-foreground">Inserisci i dati mensili per vedere l'andamento</p></CardContent></Card>
            );
            const CustomTooltip = ({ active, payload, label }) => {
              if (!active || !payload?.length) return null;
              return (
                <div className="bg-white border rounded-lg shadow-lg p-3 text-xs">
                  <div className="font-bold mb-1">{label}</div>
                  {payload.filter(p => p.value != null).map((p, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: p.color }} />
                      <span className="text-muted-foreground">{p.name}:</span>
                      <span className="font-bold tabular-nums">{p.dataKey.startsWith('mar_pct') ? fmtPct(p.value) : fmtCur(p.value)}</span>
                    </div>
                  ))}
                </div>
              );
            };
            return (<>
              {/* Revenue trend */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2"><TrendingUp className="h-4 w-4 text-emerald-600" />Andamento Fatturato per Prodotto</CardTitle>
                  <CardDescription>Fatturato mensile di ogni prodotto/servizio — utile per analizzare la stagionalita'</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={320}>
                    <AreaChart data={chartData}>
                      <defs>
                        {prodsWithData.map((p, i) => (
                          <linearGradient key={p.id} id={`grad_rev_${p.id}`} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={COLORS[i % COLORS.length]} stopOpacity={0.15} />
                            <stop offset="95%" stopColor={COLORS[i % COLORS.length]} stopOpacity={0} />
                          </linearGradient>
                        ))}
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                      {prodsWithData.map((p, i) => (
                        <Area key={p.id} type="monotone" dataKey={`rev_${p.id}`} name={p.name} stroke={COLORS[i % COLORS.length]} fill={`url(#grad_rev_${p.id})`} strokeWidth={2.5} dot={{ r: 4, fill: COLORS[i % COLORS.length] }} connectNulls />
                      ))}
                    </AreaChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Margin trend (absolute) */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2"><BarChart3 className="h-4 w-4 text-blue-600" />Andamento Margine per Prodotto</CardTitle>
                  <CardDescription>Margine mensile in EUR — se sale, stai migliorando efficienza o volumi</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={320}>
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                      {prodsWithData.map((p, i) => (
                        <Line key={p.id} type="monotone" dataKey={`mar_${p.id}`} name={p.name} stroke={COLORS[i % COLORS.length]} strokeWidth={2.5} dot={{ r: 5, fill: COLORS[i % COLORS.length], strokeWidth: 2, stroke: '#fff' }} connectNulls />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Margin % trend */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2"><TrendingUp className="h-4 w-4 text-violet-600" />Andamento Margine % per Prodotto</CardTitle>
                  <CardDescription>Margine percentuale — se migliora, stai vendendo meglio o riducendo i costi</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={320}>
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis tickFormatter={v => `${v}%`} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                      {prodsWithData.map((p, i) => (
                        <Line key={p.id} type="monotone" dataKey={`mar_pct_${p.id}`} name={p.name} stroke={COLORS[i % COLORS.length]} strokeWidth={2.5} strokeDasharray={i > 0 ? `${5 + i * 2} ${3 + i}` : ''} dot={{ r: 5, fill: COLORS[i % COLORS.length], strokeWidth: 2, stroke: '#fff' }} connectNulls />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Monthly data table */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Dettaglio Mensile</CardTitle>
                  <CardDescription>Fatturato, margine e quantita' mese per mese</CardDescription>
                </CardHeader>
                <CardContent className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Prodotto</TableHead>
                        <TableHead className="text-xs">Voce</TableHead>
                        {MONTHS.map((m, i) => <TableHead key={i} className="text-right text-xs px-2">{m}</TableHead>)}
                        <TableHead className="text-right font-bold">Totale</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {prodsWithData.map((p, pi) => (<React.Fragment key={p.id}>
                        <TableRow key={`rev-${p.id}`} className={pi > 0 ? 'border-t-2' : ''}>
                          <TableCell className="font-semibold text-xs" rowSpan={3}>
                            <div className="flex items-center gap-2">
                              <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: COLORS[pi % COLORS.length] }} />
                              {p.name}
                            </div>
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground font-medium whitespace-nowrap">Fatturato</TableCell>
                          {MONTHS.map((_, i) => {
                            const md = p.monthly_data.find(d => d.month === i + 1);
                            return <TableCell key={i} className="text-right text-xs tabular-nums px-2">{md ? fmtCur(md.revenue) : <span className="text-muted-foreground">-</span>}</TableCell>;
                          })}
                          <TableCell className="text-right text-xs font-bold tabular-nums">{fmtCur(p.total_revenue)}</TableCell>
                        </TableRow>
                        <TableRow key={`mar-${p.id}`}>
                          <TableCell className="text-xs text-muted-foreground font-medium whitespace-nowrap">Margine</TableCell>
                          {MONTHS.map((_, i) => {
                            const md = p.monthly_data.find(d => d.month === i + 1);
                            return <TableCell key={i} className={`text-right text-xs tabular-nums px-2 ${md ? (md.margin >= 0 ? 'text-emerald-700' : 'text-red-600') : ''}`}>{md ? fmtCur(md.margin) : <span className="text-muted-foreground">-</span>}</TableCell>;
                          })}
                          <TableCell className={`text-right text-xs font-bold tabular-nums ${p.actual_margin >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>{fmtCur(p.actual_margin)}</TableCell>
                        </TableRow>
                        <TableRow key={`qty-${p.id}`}>
                          <TableCell className="text-xs text-muted-foreground font-medium whitespace-nowrap">Quantità</TableCell>
                          {MONTHS.map((_, i) => {
                            const md = p.monthly_data.find(d => d.month === i + 1);
                            return <TableCell key={i} className="text-right text-xs tabular-nums px-2">{md && md.quantity != null ? md.quantity.toLocaleString('it-IT') : <span className="text-muted-foreground">-</span>}</TableCell>;
                          })}
                          <TableCell className="text-right text-xs font-bold tabular-nums">{p.total_quantity != null ? p.total_quantity.toLocaleString('it-IT') : '-'}</TableCell>
                        </TableRow>
                      </React.Fragment>))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </>);
          })()}
        </TabsContent>

        {/* ==================== RANKINGS ==================== */}
        <TabsContent value="rankings" className="space-y-4">
          {!rankings || rankings.products.length === 0 ? (
            <Card><CardContent className="py-12 text-center"><p className="text-muted-foreground">Aggiungi prodotti e dati mensili per vedere le classifiche</p></CardContent></Card>
          ) : (() => {
            const withData = rankings.products.filter(p => p.has_monthly_data);
            const standardProds = rankings.products.filter(p => p.cost_mode !== 'variable');
            const totalRev = withData.reduce((s, p) => s + p.total_revenue, 0);
            const totalMargin = withData.reduce((s, p) => s + p.actual_margin, 0);

            // Simulation: what if 100% of business was this service
            const totalHours = parseFloat(withData.reduce((s, p) => s + p.total_hours, 0).toFixed(1));
            const monthsWithData = new Set();
            withData.forEach(p => p.monthly_data?.forEach(m => monthsWithData.add(m.month)));
            const numMonths = monthsWithData.size || 1;
            const msc = rankings.summary?.monthly_structure_cost || 0;
            const annualStructure = msc * 12;
            const periodStructure = msc * numMonths;

            const simulations = withData.map(p => {
              const avgHoursPerUnit = p.total_quantity > 0 ? p.total_hours / p.total_quantity : 0;
              const avgRevPerUnit = p.total_quantity > 0 ? p.total_revenue / p.total_quantity : 0;
              const avgMarginPerUnit = p.total_quantity > 0 ? p.actual_margin / p.total_quantity : 0;
              const unitsProducible = avgHoursPerUnit > 0 ? Math.floor(totalHours / avgHoursPerUnit) : 0;
              const simRevenue = unitsProducible * avgRevPerUnit;
              const simMarginBeforeStructure = unitsProducible * avgMarginPerUnit;
              const simProfit = simMarginBeforeStructure - periodStructure;
              const simProfitPct = simRevenue > 0 ? (simProfit / simRevenue * 100) : 0;
              const sustainable = simProfit > 0;
              return { ...p, sim_units: unitsProducible, sim_revenue: simRevenue, sim_margin: simMarginBeforeStructure, sim_profit: simProfit, sim_profit_pct: simProfitPct, sustainable, avg_hours: avgHoursPerUnit, avg_rev: avgRevPerUnit, avg_margin: avgMarginPerUnit };
            }).sort((a, b) => b.sim_profit - a.sim_profit);

            // Diamond service: only from sustainable services
            const sustainableServices = simulations.filter(s => s.sustainable);
            // NUOVO TEST: break-even ticket cliente/anno.
            // Ogni cliente sopporta ~costo_to_serve/anno di FC. Assumendo che una unita venduta
            // rappresenti circa un cliente/anno, il servizio e "Diamante" solo se il PREZZO
            // MEDIO per unita copre il costo-to-serve (dato margine%).
            // Un servizio con margine 68% e prezzo/unita 55€ ha ~37€ di contributo → non copre
            // 204€ di FC/cliente → NON e Diamante ma "front-end".
            const passesBreakEven = (p) => {
              if (!wcCostToServe || wcCostToServe <= 0) return true;
              const marginPct = p.actual_margin_pct || 0;
              const pricePerUnit = p.actual_margin_per_unit && marginPct > 0
                ? (p.actual_margin_per_unit * 100 / marginPct)
                : 0;
              if (pricePerUnit <= 0) return false;
              const beTicket = wcCostToServe / (marginPct / 100);
              return pricePerUnit >= beTicket;
            };
            const diamondEligible = sustainableServices.filter(passesBreakEven);
            // NO FALLBACK: se nessun servizio passa il break-even, il diamante non esiste.
            // Meglio dire "nessun diamante trovato" che promuovere un servizio antieconomico.
            const diamondPool = diamondEligible;
            const maxMarginPct = Math.max(...diamondPool.map(p => p.actual_margin_pct), 1);
            const maxMPH = Math.max(...diamondPool.map(p => p.margin_per_hour), 1);
            const maxQty = Math.max(...diamondPool.map(p => p.total_quantity), 1);
            const scored = diamondPool.map(p => ({
              ...p,
              score: (p.actual_margin_pct / maxMarginPct) * 40 + (p.margin_per_hour / maxMPH) * 35 + (p.total_quantity / maxQty) * 25,
              score_margin: Math.round((p.actual_margin_pct / maxMarginPct) * 100),
              score_efficiency: Math.round((p.margin_per_hour / maxMPH) * 100),
              score_volume: Math.round((p.total_quantity / maxQty) * 100),
            })).sort((a, b) => b.score - a.score);
            const diamond = scored[0];
            const noDiamondSustainable = sustainableServices.length === 0;
            // Se ci sono servizi sostenibili ma nessuno passa il break-even ticket → non ho diamante
            const noDiamondBreakeven = !diamond && !noDiamondSustainable;
            // Info break-even per il diamante scelto
            const diamondBreakEvenUnits = (wcCostToServe > 0 && diamond?.actual_margin_per_unit > 0)
              ? (wcCostToServe / diamond.actual_margin_per_unit)
              : null;
            const diamondBreakEvenTicket = (wcCostToServe > 0 && diamond?.actual_margin_pct > 0)
              ? (wcCostToServe / (diamond.actual_margin_pct / 100))
              : null;
            // Servizi "front-end candidate": alto margine ma break-even oltre 3 unità/cliente
            const frontendCandidates = sustainableServices.filter(s => {
              if (!wcCostToServe || wcCostToServe <= 0) return false;
              const marginPct = s.actual_margin_pct || 0;
              const pricePerUnit = s.actual_margin_per_unit && marginPct > 0
                ? (s.actual_margin_per_unit * 100 / marginPct)
                : 0;
              if (pricePerUnit <= 0) return false;
              const beTicket = wcCostToServe / (marginPct / 100);
              return pricePerUnit < beTicket && marginPct >= 40;
            });

            const RankTable = ({ title, desc, icon: Ic, data, columns }) => (
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Ic className="h-4 w-4" />{title}</CardTitle><CardDescription>{desc}</CardDescription></CardHeader>
                <CardContent>
                  <Table><TableHeader><TableRow>
                    <TableHead className="w-8">#</TableHead><TableHead>Prodotto</TableHead>
                    {columns.map((c, i) => <TableHead key={i} className="text-right">{c.label}</TableHead>)}
                  </TableRow></TableHeader>
                  <TableBody>
                    {data.map((p, i) => {
                      // Break-even ticket badge: ✅ se il prezzo/unita copre il costo-to-serve/margine%, ❌ altrimenti
                      const marginPct = p.actual_margin_pct || 0;
                      const pricePerUnit = p.actual_margin_per_unit && marginPct > 0
                        ? (p.actual_margin_per_unit * 100 / marginPct)
                        : 0;
                      const beTicket = (wcCostToServe > 0 && marginPct > 0)
                        ? wcCostToServe / (marginPct / 100)
                        : 0;
                      const beApplicable = wcCostToServe > 0 && marginPct > 0 && pricePerUnit > 0;
                      const bePasses = beApplicable && pricePerUnit >= beTicket;
                      return (
                        <TableRow key={p.id} className={i === 0 ? 'bg-amber-50' : ''}>
                          <TableCell className="font-bold text-muted-foreground">{i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : i + 1}</TableCell>
                          <TableCell className="font-semibold">
                            <div className="flex items-center gap-2">{p.name}
                              <Badge variant="outline" className={`text-[9px] ${p.cost_mode === 'variable' ? 'bg-orange-50 text-orange-600 border-orange-200' : 'bg-blue-50 text-blue-600 border-blue-200'}`}>{p.cost_mode === 'variable' ? 'Var' : 'Std'}</Badge>
                              {beApplicable && (
                                <Badge
                                  variant="outline"
                                  data-testid={`be-badge-${p.id}`}
                                  title={bePasses
                                    ? `Sostenibile in isolamento: prezzo ${fmtCur(pricePerUnit)}/unita ≥ break-even ticket ${fmtCur(beTicket)}`
                                    : `Front-end: prezzo ${fmtCur(pricePerUnit)}/unita < break-even ticket ${fmtCur(beTicket)}`}
                                  className={`text-[9px] ${bePasses ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-700 border-red-200'}`}
                                >
                                  {bePasses ? '✅ BE' : '❌ BE'}
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          {columns.map((c, ci) => <TableCell key={ci} className={`text-right tabular-nums ${c.bold ? 'font-bold' : ''} ${c.color?.(p) || ''}`}>{c.render(p)}</TableCell>)}
                        </TableRow>
                      );
                    })}
                  </TableBody></Table>
                </CardContent>
              </Card>
            );

            return (<>
              {/* Summary cards */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <Card className="border-emerald-200"><CardContent className="pt-3 pb-2 text-center"><div className="text-[10px] uppercase text-muted-foreground">Genera margine</div><div className="text-2xl font-black text-emerald-700">{rankings.summary.profit_count}</div></CardContent></Card>
                <Card className="border-red-200"><CardContent className="pt-3 pb-2 text-center"><div className="text-[10px] uppercase text-muted-foreground">In perdita</div><div className="text-2xl font-black text-red-700">{rankings.summary.loss_count}</div></CardContent></Card>
                <Card className="border-amber-200"><CardContent className="pt-3 pb-2 text-center"><div className="text-[10px] uppercase text-muted-foreground">Gira soldi</div><div className="text-2xl font-black text-amber-700">{rankings.summary.neutral_count}</div></CardContent></Card>
                <Card><CardContent className="pt-3 pb-2 text-center"><div className="text-[10px] uppercase text-muted-foreground">Fatturato tot.</div><div className="text-lg font-black">{fmtCur(totalRev)}</div></CardContent></Card>
                <Card><CardContent className="pt-3 pb-2 text-center"><div className="text-[10px] uppercase text-muted-foreground">Margine tot.</div><div className={`text-lg font-black ${totalMargin >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>{fmtCur(totalMargin)}</div></CardContent></Card>
              </div>

              {/* FRONT-END CANDIDATES: alto margine unitario ma break-even oltre 3 unita/cliente */}
              {frontendCandidates.length > 0 && (
                <Card className="border-2 border-amber-300 bg-amber-50">
                  <CardContent className="pt-4 pb-4">
                    <div className="flex items-start gap-3">
                      <div className="shrink-0 text-3xl">⚠️</div>
                      <div className="flex-1">
                        <div className="text-[10px] uppercase tracking-widest text-amber-800 font-bold mb-0.5">Attenzione — Servizi front-end</div>
                        <h3 className="text-base font-bold text-amber-900">
                          {frontendCandidates.length === 1 ? "1 servizio con" : `${frontendCandidates.length} servizi con`} alto margine unitario ma antieconomici in isolamento
                        </h3>
                        <p className="text-xs text-amber-800 mt-1">
                          Questi servizi rendono molto per singola unita, ma un cliente che li compra "una tantum" non copre il costo-to-serve ({fmtCur(wcCostToServe)}/anno). Da usare come <strong>front-end</strong> (aggancio ad altri servizi) o venduti in <strong>pacchetto minimo</strong>.
                        </p>
                        <div className="mt-2 space-y-1">
                          {frontendCandidates.slice(0, 5).map(s => {
                            const marginPct = s.actual_margin_pct || 0;
                            const price = s.actual_margin_per_unit && marginPct > 0
                              ? (s.actual_margin_per_unit * 100 / marginPct) : 0;
                            const beTicket = marginPct > 0 ? wcCostToServe / (marginPct / 100) : 0;
                            return (
                              <div key={s.id} className="text-xs bg-white/60 rounded px-2 py-1 flex items-center justify-between">
                                <span className="font-semibold text-amber-900">{s.name}</span>
                                <span className="text-amber-700">
                                  Margine {fmtPct(s.actual_margin_pct)} — prezzo {fmtCur(price)}/unita — <strong>break-even ticket {fmtCur(beTicket)}</strong>
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* DIAMOND SERVICE */}
              {diamond && !noDiamondSustainable && (
                <Card className="border-2 border-cyan-300 bg-gradient-to-r from-cyan-50 via-white to-cyan-50 overflow-hidden relative">
                  <div className="absolute top-0 right-0 w-32 h-32 opacity-5">
                    <Gem className="w-full h-full text-cyan-900" />
                  </div>
                  <CardContent className="pt-5 pb-4">
                    <div className="flex items-start gap-4">
                      <div className="shrink-0 w-16 h-16 rounded-xl bg-gradient-to-br from-cyan-400 to-blue-600 flex items-center justify-center shadow-lg">
                        <Gem className="h-8 w-8 text-white" />
                      </div>
                      <div className="flex-1">
                        <div className="text-[10px] uppercase tracking-widest text-cyan-700 font-bold mb-0.5">Servizio Diamante</div>
                        <h3 className="text-xl font-black text-slate-900">{diamond.name}</h3>
                        <p className="text-xs text-muted-foreground mt-0.5">Il servizio con il miglior equilibrio tra marginalita, efficienza e volume</p>
                        <div className="grid grid-cols-4 gap-4 mt-3">
                          <div className="text-center"><div className="text-[10px] text-muted-foreground">Margine %</div><div className="text-lg font-black text-cyan-700">{fmtPct(diamond.actual_margin_pct)}</div></div>
                          <div className="text-center"><div className="text-[10px] text-muted-foreground">EUR/ora</div><div className="text-lg font-black text-cyan-700">{fmtCur(diamond.margin_per_hour)}</div></div>
                          <div className="text-center"><div className="text-[10px] text-muted-foreground">Margine/unita</div><div className="text-lg font-black text-cyan-700">{fmtCur(diamond.actual_margin_per_unit)}</div></div>
                          <div className="text-center"><div className="text-[10px] text-muted-foreground">Score</div><div className="text-lg font-black text-cyan-700">{Math.round(diamond.score)}/100</div></div>
                        </div>
                        {diamondBreakEvenTicket !== null && (
                          <div className="mt-3 pt-2 border-t border-cyan-200 text-[11px] text-slate-600">
                            <span className="font-semibold text-slate-700">Break-even cliente/anno:</span>{" "}
                            <span className="font-bold text-cyan-800">{fmtCur(diamondBreakEvenTicket)}</span>
                            {" "} — costo-to-serve medio {fmtCur(wcCostToServe)} × 100/{fmtPct(diamond.actual_margin_pct)}
                            {diamondBreakEvenUnits !== null && diamondBreakEvenUnits > 1 && (
                              <> · servono almeno {diamondBreakEvenUnits.toFixed(1)} unita/cliente</>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    {/* Radar chart - all services with data */}
                    {withData.length >= 2 && (() => {
                      // Score ALL services for comparison, not just diamond pool
                      const allMaxMarginPct = Math.max(...withData.map(p => p.actual_margin_pct), 1);
                      const allMaxMPH = Math.max(...withData.map(p => p.margin_per_hour), 1);
                      const allMaxQty = Math.max(...withData.map(p => p.total_quantity), 1);
                      const allScored = withData.map(p => ({
                        ...p,
                        score_margin: Math.round((p.actual_margin_pct / allMaxMarginPct) * 100),
                        score_efficiency: Math.round((p.margin_per_hour / allMaxMPH) * 100),
                        score_volume: Math.round((p.total_quantity / allMaxQty) * 100),
                      })).sort((a, b) => {
                        const scoreA = (a.actual_margin_pct / allMaxMarginPct) * 40 + (a.margin_per_hour / allMaxMPH) * 35 + (a.total_quantity / allMaxQty) * 25;
                        const scoreB = (b.actual_margin_pct / allMaxMarginPct) * 40 + (b.margin_per_hour / allMaxMPH) * 35 + (b.total_quantity / allMaxQty) * 25;
                        return scoreB - scoreA;
                      });
                      const RADAR_COLORS = ['#06B6D4', '#3B82F6', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6', '#F97316'];
                      return (
                        <div className="mt-3 flex justify-center">
                          <ResponsiveContainer width="100%" height={340}>
                            <RadarChart data={[
                              { metric: 'Margine %', ...Object.fromEntries(allScored.map(s => [s.name, s.score_margin])) },
                              { metric: 'EUR/ora', ...Object.fromEntries(allScored.map(s => [s.name, s.score_efficiency])) },
                              { metric: 'Volume', ...Object.fromEntries(allScored.map(s => [s.name, s.score_volume])) },
                            ]}>
                              <PolarGrid />
                              <PolarAngleAxis dataKey="metric" tick={{ fontSize: 10 }} />
                              <PolarRadiusAxis domain={[0, 100]} tick={false} />
                              {allScored.map((s, i) => (
                                <Radar key={s.id} name={s.name} dataKey={s.name} stroke={RADAR_COLORS[i % RADAR_COLORS.length]} fill={RADAR_COLORS[i % RADAR_COLORS.length]} fillOpacity={i === 0 ? 0.2 : 0.05} strokeWidth={i === 0 ? 2.5 : 1.5} />
                              ))}
                              <Legend wrapperStyle={{ fontSize: 10 }} />
                            </RadarChart>
                          </ResponsiveContainer>
                        </div>
                      );
                    })()}
                  </CardContent>
                </Card>
              )}
              {noDiamondSustainable && (
                <Card className="border-2 border-red-200 bg-red-50">
                  <CardContent className="pt-4 pb-3 text-center">
                    <Gem className="h-10 w-10 mx-auto text-red-300 mb-2" />
                    <h3 className="font-bold text-red-800">Nessun Servizio Diamante</h3>
                    <p className="text-sm text-red-600">Nessun servizio da solo sarebbe in grado di sostenere i costi di struttura. Il mix attuale e' necessario.</p>
                  </CardContent>
                </Card>
              )}
              {noDiamondBreakeven && (
                <Card className="border-2 border-amber-300 bg-amber-50">
                  <CardContent className="pt-4 pb-3 text-center">
                    <Gem className="h-10 w-10 mx-auto text-amber-400 mb-2" />
                    <h3 className="font-bold text-amber-900">Nessun Servizio Diamante</h3>
                    <p className="text-sm text-amber-800 max-w-2xl mx-auto">
                      Ci sono servizi sostenibili in isolamento, ma <strong>nessuno supera il test del break-even ticket cliente/anno</strong>{wcCostToServe > 0 && (<> ({fmtCur(wcCostToServe)} costo-to-serve medio)</>)}. Il prezzo per unita non copre il costo amministrativo di gestione cliente. Vedi sopra i <strong>candidati front-end</strong>.
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* 4 RANKING TABLES */}
              {withData.length > 0 && (<>
                {wcCostToServe > 0 && (
                  <div className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-md px-3 py-2 flex flex-wrap items-center gap-x-4 gap-y-1">
                    <span className="font-semibold text-slate-700">Legenda BE (Break-even ticket):</span>
                    <span className="inline-flex items-center gap-1"><Badge variant="outline" className="text-[9px] bg-emerald-50 text-emerald-700 border-emerald-200">✅ BE</Badge> sostenibile in isolamento (prezzo/unità ≥ break-even)</span>
                    <span className="inline-flex items-center gap-1"><Badge variant="outline" className="text-[9px] bg-red-50 text-red-700 border-red-200">❌ BE</Badge> front-end (prezzo/unità &lt; break-even {fmtCur(wcCostToServe)}/margine%)</span>
                  </div>
                )}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {/* 1. By total margin */}
                  <RankTable title="Per Margine Totale" desc="Chi genera piu margine in assoluto" icon={DollarSign}
                    data={[...withData].sort((a, b) => b.actual_margin - a.actual_margin)}
                    columns={[
                      { label: 'Fatturato', render: p => fmtCur(p.total_revenue) },
                      { label: 'Margine', bold: true, color: p => p.actual_margin >= 0 ? 'text-emerald-700' : 'text-red-700', render: p => fmtCur(p.actual_margin) },
                      { label: 'Margine %', render: p => fmtPct(p.actual_margin_pct) },
                    ]} />

                  {/* 2. By unit margin */}
                  <RankTable title="Per Margine Unitario" desc="Chi guadagna di piu per singola unita venduta" icon={Award}
                    data={[...withData].sort((a, b) => b.actual_margin_per_unit - a.actual_margin_per_unit)}
                    columns={[
                      { label: 'Qta', render: p => p.total_quantity },
                      { label: 'Marg/unita', bold: true, color: p => p.actual_margin_per_unit >= 0 ? 'text-emerald-700' : 'text-red-700', render: p => fmtCur(p.actual_margin_per_unit) },
                      { label: 'Margine %', render: p => fmtPct(p.actual_margin_pct) },
                    ]} />

                  {/* 3. By quantity sold */}
                  <RankTable title="Per Quantita Vendute" desc="I servizi piu richiesti dai clienti" icon={Users}
                    data={[...withData].sort((a, b) => b.total_quantity - a.total_quantity)}
                    columns={[
                      { label: 'Qta', bold: true, render: p => p.total_quantity },
                      { label: 'Fatturato', render: p => fmtCur(p.total_revenue) },
                      { label: 'Margine %', color: p => p.actual_margin_pct >= 15 ? 'text-emerald-700' : p.actual_margin_pct < 0 ? 'text-red-700' : '', render: p => fmtPct(p.actual_margin_pct) },
                    ]} />

                  {/* 4. By hours worked - fewer hours = better */}
                  <RankTable title="Per Ore Lavorate" desc="Chi lavora meno ore per generare margine" icon={Timer}
                    data={[...withData].sort((a, b) => a.total_hours - b.total_hours)}
                    columns={[
                      { label: 'Ore', bold: true, render: p => `${p.total_hours}h` },
                      { label: 'EUR/ora', bold: true, color: p => p.margin_per_hour >= 30 ? 'text-emerald-700' : 'text-amber-700', render: p => fmtCur(p.margin_per_hour) },
                      { label: 'Margine %', render: p => fmtPct(p.actual_margin_pct) },
                    ]} />
                </div>
              </>)}

              {/* SIMULATION: What if 100% was this service */}
              {simulations.length > 0 && (
                <div className="space-y-4 mt-2">
                  <div>
                    <h3 className="text-lg font-bold flex items-center gap-2"><BarChart3 className="h-5 w-5 text-violet-600" />Simulazione: e se il business fosse solo un servizio?</h3>
                    <p className="text-sm text-muted-foreground">Ore totali lavorate nel periodo ({numMonths} {numMonths === 1 ? 'mese' : 'mesi'}): <strong>{totalHours}h</strong>. Cosa succederebbe dedicandole tutte a un singolo servizio? Costo struttura: <strong>{fmtCur(msc)}/mese</strong> ({numMonths} mesi = {fmtCur(periodStructure)})</p>
                  </div>
                  {msc === 0 && (
                    <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800">Imposta il "Costo struttura mensile" in alto per vedere l'utile finale di ogni simulazione (affitto, stipendi, utenze, etc.)</div>
                  )}
                  <div className="grid grid-cols-1 gap-3">
                    {simulations.map((p, i) => {
                      const diffProfit = p.sim_profit - (totalMargin - periodStructure);
                      return (
                        <Card key={p.id} className={`border-2 ${p.sustainable ? 'border-emerald-200' : 'border-red-200'}`}>
                          <CardContent className="pt-4 pb-3">
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center gap-2">
                                <span className="text-lg font-black text-muted-foreground">{i === 0 ? '🏆' : `#${i + 1}`}</span>
                                <div>
                                  <h4 className="font-bold text-base">{p.name}</h4>
                                  <p className="text-xs text-muted-foreground">Se tutte le {totalHours}h fossero dedicate a questo servizio</p>
                                </div>
                              </div>
                              <Badge className={`text-sm px-3 py-1 ${p.sustainable ? 'bg-emerald-100 text-emerald-800 border-emerald-300' : 'bg-red-100 text-red-800 border-red-300'}`}>
                                {p.sustainable ? 'Sostenibile' : 'Non sostenibile'}
                              </Badge>
                            </div>
                            {/* Flow: Hours → Units → Revenue → Margin → Structure → Profit */}
                            <div className="grid grid-cols-6 gap-1 text-center">
                              <div className="p-2 rounded bg-slate-50">
                                <div className="text-[10px] text-muted-foreground uppercase">Ore lavorate</div>
                                <div className="text-lg font-black">{totalHours}h</div>
                              </div>
                              <div className="p-2 rounded bg-slate-50 relative">
                                <div className="text-[10px] text-muted-foreground uppercase">Unita producibili</div>
                                <div className="text-lg font-black">{p.sim_units.toLocaleString('it-IT')}</div>
                                <div className="text-[9px] text-muted-foreground">({p.avg_hours.toFixed(1)}h cad.)</div>
                              </div>
                              <div className="p-2 rounded bg-blue-50">
                                <div className="text-[10px] text-muted-foreground uppercase">Fatturato</div>
                                <div className="text-lg font-black text-blue-700">{fmtCur(p.sim_revenue)}</div>
                                <div className="text-[9px] text-muted-foreground">({fmtCur(p.avg_rev)} cad.)</div>
                              </div>
                              <div className="p-2 rounded bg-emerald-50">
                                <div className="text-[10px] text-muted-foreground uppercase">Margine lordo</div>
                                <div className="text-lg font-black text-emerald-700">{fmtCur(p.sim_margin)}</div>
                                <div className="text-[9px] text-muted-foreground">({fmtPct(p.total_revenue > 0 ? p.actual_margin / p.total_revenue * 100 : 0)})</div>
                              </div>
                              <div className="p-2 rounded bg-orange-50">
                                <div className="text-[10px] text-muted-foreground uppercase">Struttura</div>
                                <div className="text-lg font-black text-orange-700">-{fmtCur(periodStructure)}</div>
                                <div className="text-[9px] text-muted-foreground">{fmtCur(msc)}/mese</div>
                              </div>
                              <div className={`p-2 rounded ${p.sustainable ? 'bg-emerald-100' : 'bg-red-100'}`}>
                                <div className="text-[10px] text-muted-foreground uppercase">Utile finale</div>
                                <div className={`text-lg font-black ${p.sustainable ? 'text-emerald-800' : 'text-red-800'}`}>{fmtCur(p.sim_profit)}</div>
                                <div className={`text-[9px] font-bold ${diffProfit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{diffProfit >= 0 ? '+' : ''}{fmtCur(diffProfit)} vs mix</div>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Standard products: theoretical unit margin */}
              {standardProds.length > 0 && (
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-base">Margine Unitario Teorico (solo standard)</CardTitle><CardDescription>Quanto guadagni vendendo 1 unita — basato sulla definizione del prodotto</CardDescription></CardHeader>
                  <CardContent>
                    <Table><TableHeader><TableRow>
                      <TableHead>#</TableHead><TableHead>Prodotto</TableHead><TableHead className="text-right">Prezzo</TableHead><TableHead className="text-right">Costo</TableHead><TableHead className="text-right">Margine unit.</TableHead><TableHead className="text-right">Margine %</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {[...standardProds].sort((a, b) => b.theoretical_margin - a.theoretical_margin).map((p, i) => (
                        <TableRow key={p.id}>
                          <TableCell className="font-bold text-muted-foreground">{i + 1}</TableCell>
                          <TableCell className="font-semibold">{p.name}</TableCell>
                          <TableCell className="text-right tabular-nums">{fmtCur(p.selling_price)}</TableCell>
                          <TableCell className="text-right tabular-nums">{fmtCur(p.selling_price - p.theoretical_margin)}</TableCell>
                          <TableCell className={`text-right tabular-nums font-bold ${p.theoretical_margin >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>{fmtCur(p.theoretical_margin)}</TableCell>
                          <TableCell className="text-right tabular-nums">{fmtPct(p.theoretical_margin_pct)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody></Table>
                  </CardContent>
                </Card>
              )}
            </>);
          })()}
        </TabsContent>

        {/* ==================== BUNDLE / MIX OPTIMIZER ==================== */}
        <TabsContent value="bundles" className="space-y-4">
          {/* BUNDLES SECTION - always available */}
          {bundles.length > 0 && (
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Package className="h-4 w-4" />Bundle Creati</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                {bundles.map(b => (
                  <div key={b.id} className="border rounded-lg p-3" data-testid={`bundle-card-${b.id}`}>
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h4 className="font-bold">{b.name}</h4>
                        <p className="text-xs text-muted-foreground">{b.items_detail?.map(it => `${it.name} x${it.quantity}`).join(', ')}</p>
                      </div>
                      <Button size="sm" variant="ghost" onClick={() => deleteBundle(b.id)} data-testid={`delete-bundle-${b.id}`}><Trash2 className="h-3.5 w-3.5 text-red-400" /></Button>
                    </div>
                    <div className="grid grid-cols-4 gap-2 text-center text-xs">
                      <div><div className="text-muted-foreground">Prezzi singoli</div><div className="font-bold tabular-nums">{fmtCur(b.total_individual_price)}</div></div>
                      <div><div className="text-muted-foreground">Prezzo bundle</div><div className="font-bold tabular-nums">{fmtCur(b.bundle_price)}</div></div>
                      <div><div className="text-muted-foreground">Costo totale</div><div className="font-bold tabular-nums">{fmtCur(b.total_cost)}</div></div>
                      <div><div className="text-muted-foreground">Margine</div><div className={`font-black text-base ${b.margin >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>{fmtCur(b.margin)} <span className="text-xs">({fmtPct(b.margin_pct)})</span></div></div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {products.length >= 2 && (
            <div className="space-y-3">
              {/* Suggested bundles - auto-generated from category pairs sorted by margin/hour */}
              {(() => {
                const combos = [];
                for (let i = 0; i < products.length; i++) {
                  for (let j = i + 1; j < products.length; j++) {
                    const a = products[i], b = products[j];
                    const totalMargin = (a.margin || 0) + (b.margin || 0);
                    const totalHrs = (a.hours_per_unit || 0) + (b.hours_per_unit || 0);
                    const marginPerHour = totalHrs > 0 ? totalMargin / totalHrs : 0;
                    const totalPrice = (a.selling_price || 0) + (b.selling_price || 0);
                    const totalDur = Math.round(((a.hours_per_unit || 0) + (b.hours_per_unit || 0)) * 60);
                    combos.push({ a, b, totalMargin, marginPerHour, totalPrice, totalDur });
                  }
                }
                combos.sort((x, y) => y.marginPerHour - x.marginPerHour);
                const top3 = combos.slice(0, 3);
                if (top3.length === 0) return null;
                return (
                  <Card>
                    <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Award className="h-4 w-4 text-amber-500" />Bundle Suggeriti (massimo margine/ora)</CardTitle></CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                        {top3.map((c, idx) => (
                          <div key={idx} className={`p-3 rounded-lg border-2 ${idx === 0 ? 'border-amber-300 bg-amber-50' : 'border-gray-200'}`}>
                            <div className="flex items-center gap-1 mb-1">
                              {idx === 0 && <Award className="h-4 w-4 text-amber-500" />}
                              <span className="font-bold text-sm">{c.a.name} + {c.b.name}</span>
                            </div>
                            <div className="grid grid-cols-2 gap-1 text-[10px]">
                              <div className="text-muted-foreground">Prezzo: <span className="font-bold text-foreground">{fmtCur(c.totalPrice)}</span></div>
                              <div className="text-muted-foreground">Durata: <span className="font-bold text-foreground">{c.totalDur} min</span></div>
                              <div className="text-muted-foreground">Margine: <span className="font-bold text-emerald-700">{fmtCur(c.totalMargin)}</span></div>
                              <div className="text-muted-foreground">Margine/h: <span className="font-bold text-emerald-700">{fmtCur(c.marginPerHour)}</span></div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                );
              })()}
              <div className="flex justify-center">
                <Button onClick={() => setShowBundle(true)} data-testid="add-bundle-inline-btn"><Package className="h-4 w-4 mr-2" />Crea Nuovo Bundle</Button>
              </div>
            </div>
          )}

          {/* MIX OPTIMIZER SECTION - requires monthly data */}
          {(() => {
            const withData = rankings?.products?.filter(p => p.has_monthly_data && p.total_quantity > 0 && p.total_hours > 0) || [];
            if (withData.length < 2) return (
              <Card className="border-dashed"><CardContent className="py-8 text-center"><BarChart3 className="h-10 w-10 mx-auto text-muted-foreground mb-2" /><h3 className="text-sm font-semibold text-muted-foreground">Ottimizzatore Mix</h3><p className="text-xs text-muted-foreground">Inserisci i dati mensili di almeno 2 prodotti per attivare l'ottimizzatore del mix</p></CardContent></Card>
            );

            const totalHours = parseFloat(withData.reduce((s, p) => s + p.total_hours, 0).toFixed(1));
            const monthsWithData = new Set();
            withData.forEach(p => p.monthly_data?.forEach(m => monthsWithData.add(m.month)));
            const numMonths = monthsWithData.size || 1;
            const msc = rankings.summary?.monthly_structure_cost || 0;
            const periodStructure = msc * numMonths;

            // Metrics per service
            const metrics = withData.map(p => ({
              ...p,
              margin_per_hour: p.total_hours > 0 ? p.actual_margin / p.total_hours : 0,
              rev_per_hour: p.total_hours > 0 ? p.total_revenue / p.total_hours : 0,
              hours_pct: totalHours > 0 ? (p.total_hours / totalHours * 100) : 0,
            }));

            // Pre-built strategies
            const currentAlloc = {};
            metrics.forEach(p => { currentAlloc[p.id] = Math.round(p.hours_pct); });

            // Optimal: allocate by margin/hour (greedy)
            const byMPH = [...metrics].sort((a, b) => b.margin_per_hour - a.margin_per_hour);
            const optimalAlloc = {};
            let remainPct = 100;
            byMPH.forEach((p, i) => {
              if (i === byMPH.length - 1) { optimalAlloc[p.id] = Math.max(0, remainPct); }
              else { const alloc = Math.min(remainPct, 70); optimalAlloc[p.id] = alloc; remainPct -= alloc; }
            });

            // Balanced
            const balancedAlloc = {};
            const equalShare = Math.floor(100 / metrics.length);
            metrics.forEach((p, i) => { balancedAlloc[p.id] = i === 0 ? 100 - equalShare * (metrics.length - 1) : equalShare; });

            const COLORS_MIX = ['#10B981','#3B82F6','#F59E0B','#EF4444','#8B5CF6','#EC4899','#14B8A6','#F97316'];

            const calcMix = (alloc) => {
              let totRev = 0, totMargin = 0, totHrs = 0;
              const details = metrics.map((p, i) => {
                const pct = (alloc[p.id] || 0) / 100;
                const hrs = totalHours * pct;
                const hrsPerUnit = p.total_hours / p.total_quantity;
                const units = hrsPerUnit > 0 ? Math.floor(hrs / hrsPerUnit) : 0;
                const revPerUnit = p.total_quantity > 0 ? p.total_revenue / p.total_quantity : 0;
                const marginPerUnit = p.total_quantity > 0 ? p.actual_margin / p.total_quantity : 0;
                const rev = units * revPerUnit;
                const margin = units * marginPerUnit;
                totRev += rev; totMargin += margin; totHrs += hrs;
                return { ...p, alloc_pct: alloc[p.id] || 0, hours: hrs, units, revenue: rev, margin, color: COLORS_MIX[i % COLORS_MIX.length] };
              });
              const profit = totMargin - periodStructure;
              return { details, totRev, totMargin, profit, totHrs, sustainable: profit > 0 };
            };

            return <MixOptimizer metrics={metrics} calcMix={calcMix} currentAlloc={currentAlloc} optimalAlloc={optimalAlloc} balancedAlloc={balancedAlloc} totalHours={totalHours} periodStructure={periodStructure} msc={msc} numMonths={numMonths} colors={COLORS_MIX} />;
          })()}
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      <ProductFormDialog open={showProductForm} onClose={() => { setShowProductForm(false); setEditProduct(null); }} onSave={saveProduct} product={editProduct} hourlyRate={hourlyRate} />
      <MonthlyDataDialog open={showMonthly} onClose={() => { setShowMonthly(false); fetchAll(); }} product={monthlyProduct} clientId={clientId} year={year} />
      <BundleDialog open={showBundle} onClose={() => setShowBundle(false)} onSave={saveBundle} products={products} catalog={catalog} hourlyRate={hourlyRate} />

      {/* Spiragli Excel Import Dialog */}
      <Dialog open={showImportExcel} onOpenChange={(o) => { if (!o) { setShowImportExcel(false); setImportPreview(null); setImportFile(null); } }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Importa "Veduta Statistico-Finanziaria" Spiragli</DialogTitle>
          </DialogHeader>
          {!importPreview && (
            <div className="space-y-4">
              <div className="text-sm text-muted-foreground">
                Carica il file Excel mensile per popolare automaticamente i dati di volume e ricavi
                dei 6 prodotti (Massaggi, Corpo, Viso, Mani, Pedicure, Ceretta) e i clienti mensili.
              </div>
              <Input
                type="file"
                accept=".xlsx,.xls"
                onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                data-testid="spiragli-excel-file-input"
              />
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowImportExcel(false)}>Annulla</Button>
                <Button
                  disabled={!importFile || importBusy}
                  onClick={async () => {
                    setImportBusy(true);
                    try {
                      const fd = new FormData();
                      fd.append('file', importFile, importFile.name);
                      const res = await axios.post(
                        `${API}/clients/${clientId}/products/spiragli-excel/preview`,
                        fd,
                        { headers: { 'Content-Type': 'multipart/form-data' } },
                      );
                      setImportPreview(res.data);
                      // Pre-select only months with data
                      const sel = {};
                      Object.keys(res.data.months).forEach(m => {
                        const obj = res.data.months[m];
                        const hasData = Object.values(obj.products).some(p => p.quantity > 0 || p.revenue > 0);
                        sel[m] = hasData;
                      });
                      setImportSelectedMonths(sel);
                    } catch (e) {
                      toast.error(e.response?.data?.detail || 'Errore parsing Excel');
                    } finally {
                      setImportBusy(false);
                    }
                  }}
                  data-testid="spiragli-excel-analyze-btn"
                >
                  {importBusy ? 'Caricamento...' : 'Analizza Excel'}
                </Button>
              </div>
            </div>
          )}

          {importPreview && (
            <div className="space-y-3">
              <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-3 py-2">
                Anno rilevato: <b>{importPreview.year}</b>. Trovati <b>{Object.keys(importPreview.matched_products).length}/6</b> prodotti collegati al catalogo esistente.
                {importPreview.unmatched_products.length > 0 && (
                  <div className="text-amber-700 mt-1">⚠ Non trovati: {importPreview.unmatched_products.join(', ')}</div>
                )}
              </div>

              <div className="text-xs">
                <b>Mappatura colonne Excel:</b>
                <ul className="list-disc list-inside text-muted-foreground mt-1">
                  <li><b>Massaggi</b>: Massaggi (trattamenti)</li>
                  <li><b>Corpo</b>: CellStop + Trattamenti Corpo + CellStop retail</li>
                  <li><b>Viso</b>: RugheStop + RugheStop retail + Spiragli di Luce</li>
                  <li><b>Manicure (Mani)</b>: Manicure + Semipermanente Mani</li>
                  <li><b>Pedicure (Piedi)</b>: Pedicure + Semipermanente Piedi</li>
                  <li><b>Ceretta</b>: EpilaTi + Esmeralda + EpilaTi retail</li>
                </ul>
              </div>

              <div className="overflow-x-auto border rounded">
                <table className="w-full text-xs">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="px-2 py-1 text-left">
                        <input type="checkbox" checked={Object.values(importSelectedMonths).every(v => v)} onChange={(e) => {
                          const sel = {};
                          Object.keys(importPreview.months).forEach(m => { sel[m] = e.target.checked; });
                          setImportSelectedMonths(sel);
                        }} /> Mese
                      </th>
                      <th className="px-2 py-1 text-right">Clienti</th>
                      {['Massaggi','Corpo','Viso','Manicure','Pedicure','Ceretta'].map(p => (
                        <th key={p} className="px-2 py-1 text-right">{p}</th>
                      ))}
                      <th className="px-2 py-1 text-right">Tot €</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.keys(importPreview.months).sort((a,b)=>Number(a)-Number(b)).map(m => {
                      const obj = importPreview.months[m];
                      const hasData = Object.values(obj.products).some(p => p.quantity > 0 || p.revenue > 0);
                      const totRev = Object.values(obj.products).reduce((s, p) => s + p.revenue, 0);
                      return (
                        <tr key={m} className={`border-t ${!hasData ? 'opacity-40' : ''}`}>
                          <td className="px-2 py-1">
                            <label className="flex items-center gap-1 cursor-pointer">
                              <input type="checkbox" checked={!!importSelectedMonths[m]} onChange={(e) => setImportSelectedMonths(s => ({ ...s, [m]: e.target.checked }))} disabled={!hasData} />
                              {MONTHS[Number(m)-1]}/{importPreview.year}
                            </label>
                          </td>
                          <td className="px-2 py-1 text-right">{obj.kpi.passaggi_cassa || '-'}</td>
                          {['Massaggi','Corpo','Viso','Manicure','Pedicure','Ceretta'].map(pn => {
                            const pp = obj.products[pn];
                            return (
                              <td key={pn} className="px-2 py-1 text-right">
                                {pp.quantity > 0 ? <><b>{pp.quantity}</b><span className="text-muted-foreground"> · {fmtCur(pp.revenue)}</span></> : '-'}
                              </td>
                            );
                          })}
                          <td className="px-2 py-1 text-right font-semibold">{totRev > 0 ? fmtCur(totRev) : '-'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="flex justify-between items-center pt-2 border-t">
                <Button variant="outline" size="sm" onClick={() => { setImportPreview(null); setImportFile(null); }}>Indietro</Button>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setShowImportExcel(false)}>Annulla</Button>
                  <Button
                    disabled={importBusy || !Object.values(importSelectedMonths).some(v => v)}
                    onClick={async () => {
                      setImportBusy(true);
                      try {
                        const selectedM = Object.keys(importSelectedMonths).filter(k => importSelectedMonths[k]).map(Number);
                        const res = await axios.post(`${API}/clients/${clientId}/products/spiragli-excel/apply`, {
                          year: importPreview.year,
                          months: selectedM,
                          product_id_map: importPreview.matched_products,
                          months_data: importPreview.months,
                        });
                        toast.success(res.data.message);
                        setShowImportExcel(false);
                        setImportPreview(null);
                        setImportFile(null);
                        fetchAll();
                      } catch (e) {
                        toast.error(e.response?.data?.detail || 'Errore salvataggio');
                      } finally {
                        setImportBusy(false);
                      }
                    }}
                    data-testid="spiragli-excel-apply-btn"
                  >
                    {importBusy ? 'Salvataggio...' : `Salva ${Object.values(importSelectedMonths).filter(v=>v).length} mesi`}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProductMarginPage;
